// WelcomeOverlay — a 1.6s cinematic editorial moment that plays the first
// time a buyer lands on a Library page after completing checkout.
//
// Triggered by `?welcome=1&to=FirstName` in the URL. After the animation
// completes (or the user taps to dismiss), the params are stripped from the
// URL so a refresh won't replay the overlay.
//
// Design notes:
//   • Full-screen ink wash with a soft grain — never harsh
//   • Three editorial beats: kicker → name (italic serif, huge) → caption
//   • Auto-fades into the page beneath at ~1600ms, click-anywhere to skip
//   • Honors prefers-reduced-motion (skips entirely)
//
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const SHOW_MS = 1600;

export default function WelcomeOverlay({ editionTitle }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState("");

  // Detect the trigger and respect reduced motion
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("welcome") !== "1") return;
    const firstName = (params.get("to") || "friend").trim();
    setTo(firstName);

    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      // Still strip the params but skip the animation
      params.delete("welcome");
      params.delete("to");
      navigate(
        { pathname: location.pathname, search: params.toString() ? `?${params}` : "" },
        { replace: true }
      );
      return;
    }

    setOpen(true);
    const dismiss = () => {
      setOpen(false);
      // Strip the params after the exit animation completes (matches transition delay)
      setTimeout(() => {
        const fresh = new URLSearchParams(location.search);
        fresh.delete("welcome");
        fresh.delete("to");
        navigate(
          { pathname: location.pathname, search: fresh.toString() ? `?${fresh}` : "" },
          { replace: true }
        );
      }, 500);
    };
    const t = setTimeout(dismiss, SHOW_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTap = () => setOpen(false);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="welcome-overlay"
          onClick={onTap}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 0.61, 0.36, 1] }}
          className="fixed inset-0 z-[80] grid place-items-center bg-[#1c1c1e] text-cream cursor-pointer"
          data-testid="welcome-overlay"
          aria-label="Welcome"
        >
          {/* Subtle grain layer */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.06] pointer-events-none mix-blend-screen"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
            }}
          />

          {/* Editorial composition */}
          <div className="relative px-8 max-w-3xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05, duration: 0.45 }}
              className="overline text-[11px] tracking-[0.4em] opacity-70 mb-6"
            >
              M Media · An Editorial House
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.55, ease: [0.22, 0.61, 0.36, 1] }}
              className="font-serif italic leading-[1.02] text-5xl sm:text-6xl md:text-7xl"
              style={{ fontFamily: 'Georgia, "Playfair Display", serif' }}
            >
              Welcome, {to}.
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 0.6, scaleX: 1 }}
              transition={{ delay: 0.35, duration: 0.6 }}
              className="h-px bg-cream/70 mx-auto mt-7 mb-6 w-24 origin-center"
            />

            <motion.p
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.5 }}
              className="text-sm sm:text-base font-serif italic opacity-80 max-w-md mx-auto"
            >
              {editionTitle
                ? <>The first page of <em className="not-italic font-semibold">{editionTitle}</em> is waiting.</>
                : "Your shelf is open."}
            </motion.p>
          </div>

          {/* Tiny "tap to skip" cue, low contrast */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.35 }}
            transition={{ delay: 0.9, duration: 0.4 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 overline text-[10px] tracking-[0.3em]"
          >
            Tap to skip
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
